import { FC, KeyboardEvent, useMemo, useRef, useState } from 'react';
import {
    MdAdd,
    MdCheck,
    MdClose,
    MdDeleteOutline,
    MdEditNote,
    MdSaveAlt,
    MdSearch,
    MdStarOutline,
    MdStar,
    MdViewWeek,
} from 'react-icons/md';
import { AnimatePresence, motion } from 'framer-motion';
import { IGridStateSettings } from '@models/response/IGridStateSettings';
import { IDataGridStateService } from '@services/IDataGridStateService';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import Modal from '@srm/ui/modal';

import './StateModal.scss';
import Tooltip from '@srm/ui/tooltip';
import MessageDialog from '@srm/ui/message-dialog';
import { sendNotification } from '@srm/ui/lib/notifications';
import { errorText, errorTraceId } from '@srm/core/errors';

export interface IStateModalProps {
    opened: boolean;
    stateSettings?: IGridStateSettings[];
    onClose: () => void;
    /** Перезагрузка данных списка после применения состояния (спискам вложений не нужна). */
    onRefreshGrid?: () => void;
    onSelectState: (stateData: IGridStateSettings[] | undefined, apply?: boolean) => void;
    /** Отличия от выбранной основы; undefined — прежний режим для других списков. */
    changes?: string[];
    /** Снимок текущего состояния списка: его делает сам список. */
    captureState: (name: string, previous?: IGridStateSettings) => IGridStateSettings;
    stateKey: string;
    stateService: IDataGridStateService;
}

export interface IStateModalConfirmProps {
    opened?: boolean;
    message?: string;
    onSuccess?: () => void;
}

const SEARCH_THRESHOLD = 6;

const StateModal: FC<IStateModalProps> = ({ ...props }: IStateModalProps) => {
    const { stateSettings, stateService, changes } = props;
    const isModified = Boolean(changes?.length);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const [inputNameValue, setInputNameValue] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');
    const [confirmModalProps, setConfirmModalProps] = useState<IStateModalConfirmProps>();
    const composeInputRef = useRef<HTMLInputElement | null>(null);

    const settingsCount = stateSettings?.length ?? 0;
    const selectedState = stateSettings?.find((s) => s.selected);
    const defaultState = stateSettings?.find((s) => s.default);

    const saveSettings = (stateKey: string, settings: IGridStateSettings[], onSuccess: () => void) => {
        if (savingRef.current) return Promise.resolve(false);
        savingRef.current = true;
        setSaving(true);
        return stateService
            .saveState(stateKey, settings)
            .then(() => {
                onSuccess();
                return true;
            })
            .catch((error: unknown) => {
                // без этого отказ выглядел как успех: список состояний в окне
                // перерисовывался, а на сервере ничего не менялось
                sendNotification({
                    message: `Состояние таблицы не сохранилось. ${errorText(error, 'Повторите сохранение.')}`,
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
                return false;
            })
            .finally(() => {
                savingRef.current = false;
                setSaving(false);
            });
    };

    const onSaveState = async () => {
        const trimmed = inputNameValue.trim();
        if (!trimmed || saving) return;

        const settingsArray = (stateSettings ?? []).map((state) => ({
            ...state,
            selected: changes !== undefined ? false : state.selected,
        }));

        if (settingsArray.some((val) => val.name === trimmed)) {
            sendNotification({
                message: 'Состояние с таким названием уже существует',
                variant: 'red',
            });
            return;
        }

        settingsArray.push({ ...props.captureState(trimmed), selected: changes !== undefined });

        if (await saveSettings(props.stateKey, settingsArray, () => props.onSelectState(settingsArray))) {
            setInputNameValue('');
            composeInputRef.current?.focus();
        }
    };

    const applyState = (index: number) => {
        if (!stateSettings) return;
        const next = stateSettings.map((s, i) => ({ ...s, selected: i === index }));
        props.onSelectState(next, true);
    };

    const overwriteWithCurrent = (index: number) => {
        if (!stateSettings) return;
        const settings = stateSettings.map((state) => ({ ...state }));
        setConfirmModalProps({
            opened: true,
            message: `Перезаписать состояние «${settings[index].name}» текущими настройками таблицы?`,
            onSuccess: () => {
                settings[index] = props.captureState(settings[index].name, settings[index]);
                void saveSettings(props.stateKey, settings, () => props.onSelectState(settings));
            },
        });
    };

    const toggleDefault = (index: number) => {
        if (!stateSettings) return;
        const settings = stateSettings.map((state, i) => ({
            ...state,
            default: i === index && !stateSettings[index].default,
        }));

        void saveSettings(props.stateKey, settings, () => props.onSelectState(settings));
    };

    const deleteState = (index: number) => {
        if (!stateSettings) return;
        const item = stateSettings[index];
        const settings = stateSettings.map((state) => ({ ...state }));
        setConfirmModalProps({
            opened: true,
            message: `Удалить состояние «${item.name}»? Это действие нельзя отменить.`,
            onSuccess: () => {
                settings.splice(index, 1);
                void saveSettings(props.stateKey, settings, () => {
                    props.onSelectState(settings);
                    if (item.selected && changes === undefined) props.onRefreshGrid?.();
                });
            },
        });
    };

    const startRename = (index: number) => {
        if (!stateSettings) return;
        setEditingIndex(index);
        setEditingValue(stateSettings[index].name);
    };

    const commitRename = () => {
        if (savingRef.current) return;
        if (editingIndex === null || !stateSettings) {
            setEditingIndex(null);
            return;
        }
        const trimmed = editingValue.trim();
        const current = stateSettings[editingIndex];

        if (!trimmed || trimmed === current.name) {
            setEditingIndex(null);
            return;
        }

        if (stateSettings.some((s, i) => i !== editingIndex && s.name === trimmed)) {
            sendNotification({
                message: 'Состояние с таким названием уже существует',
                variant: 'red',
            });
            return;
        }

        const settings = stateSettings.map((state) => ({ ...state }));
        settings[editingIndex] = { ...current, name: trimmed };
        void saveSettings(props.stateKey, settings, () => {
            props.onSelectState(settings);
            setEditingIndex(null);
        });
    };

    const onComposeKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            void onSaveState();
        }
    };

    const onRenameKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitRename();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingIndex(null);
        }
    };

    const filteredList = useMemo(() => {
        if (!stateSettings) return [];
        const q = searchValue.trim().toLowerCase();
        return stateSettings
            .map((item, originalIndex) => ({ item, originalIndex }))
            .filter(({ item }) => !q || item.name.toLowerCase().includes(q));
    }, [stateSettings, searchValue]);

    const showSearch = settingsCount >= SEARCH_THRESHOLD;
    const duplicateName = !!inputNameValue.trim() && !!stateSettings?.some((s) => s.name === inputNameValue.trim());

    const confirmModal = confirmModalProps && confirmModalProps.opened && (
        <MessageDialog
            header="Подтверждение действия"
            showOkButton={true}
            showCancelButton={true}
            message={confirmModalProps.message!}
            onConfirm={() => {
                confirmModalProps.onSuccess?.();
                setConfirmModalProps(undefined);
                return new Promise<void>((resolve) => resolve());
            }}
            onCancel={() => setConfirmModalProps(undefined)}
        />
    );

    const renderRow = (item: IGridStateSettings, originalIndex: number) => {
        const isEditing = editingIndex === originalIndex;
        const isSelected = !!item.selected;
        const isDefault = !!item.default;

        return (
            <motion.li
                key={`state-row-${item.name}-${originalIndex}`}
                layout
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                className={clsx(
                    'state-modal__row',
                    isSelected && 'state-modal__row--selected',
                    isEditing && 'state-modal__row--editing',
                )}
                onClick={() => {
                    if (isEditing) return;
                    if (!saving && !isSelected) applyState(originalIndex);
                }}
                onKeyDown={(e) => {
                    if (e.target !== e.currentTarget || isEditing || saving) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (!isSelected) applyState(originalIndex);
                    }
                }}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (!isEditing && !saving) startRename(originalIndex);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Применить состояние ${item.name}`}
            >
                <span className="state-modal__row-mark" aria-hidden>
                    <MdCheck size={14} />
                </span>

                <div className="state-modal__row-body">
                    {isEditing ? (
                        <input
                            autoFocus
                            className="state-modal__rename-input"
                            value={editingValue}
                            readOnly={saving}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={onRenameKey}
                            // Escape отменяет переименование, а не закрывает модалку
                            data-mantine-stop-propagation="true"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Новое название состояния"
                        />
                    ) : (
                        <div className="state-modal__row-name">
                            <span className="state-modal__row-name-text">{item.name}</span>
                            {isDefault && (
                                <span className="state-modal__badge">
                                    <MdStar />
                                    по умолчанию
                                </span>
                            )}
                        </div>
                    )}
                    {!isEditing && (
                        <div className="state-modal__row-meta">
                            {isSelected
                                ? isModified
                                    ? 'На основе состояния · есть изменения'
                                    : 'Применено сейчас'
                                : 'Двойной клик — переименовать'}
                        </div>
                    )}
                </div>

                <div
                    className="state-modal__row-actions"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    <Tooltip background="black" openDelay={250} label="Переименовать">
                        <button
                            className="state-modal__icon-btn"
                            disabled={saving}
                            type="button"
                            aria-label="Переименовать"
                            onClick={() => startRename(originalIndex)}
                        >
                            <MdEditNote size={18} />
                        </button>
                    </Tooltip>
                    <Tooltip background="black" openDelay={250} label="Перезаписать текущим видом">
                        <button
                            className="state-modal__icon-btn state-modal__icon-btn--save"
                            type="button"
                            disabled={saving}
                            aria-label="Перезаписать текущим состоянием таблицы"
                            onClick={() => overwriteWithCurrent(originalIndex)}
                        >
                            <MdSaveAlt size={18} />
                        </button>
                    </Tooltip>
                    <Tooltip
                        background="black"
                        openDelay={250}
                        label={isDefault ? 'Убрать «по умолчанию»' : 'Сделать «по умолчанию»'}
                    >
                        <button
                            className={clsx(
                                'state-modal__icon-btn',
                                'state-modal__icon-btn--default',
                                isDefault && 'state-modal__icon-btn--default-active',
                            )}
                            type="button"
                            disabled={saving}
                            aria-label={isDefault ? 'Убрать «по умолчанию»' : 'Сделать «по умолчанию»'}
                            onClick={() => toggleDefault(originalIndex)}
                        >
                            {isDefault ? <MdStar size={18} /> : <MdStarOutline size={18} />}
                        </button>
                    </Tooltip>
                    <Tooltip background="black" openDelay={250} label="Удалить">
                        <button
                            className="state-modal__icon-btn state-modal__icon-btn--danger"
                            type="button"
                            disabled={saving}
                            aria-label="Удалить состояние"
                            onClick={() => deleteState(originalIndex)}
                        >
                            <MdDeleteOutline size={18} />
                        </button>
                    </Tooltip>
                </div>
            </motion.li>
        );
    };

    const modal = props.opened && (
        <Modal
            size="l"
            onClose={props.onClose}
            closeDisabled={saving}
            hasUnsavedInput={() =>
                inputNameValue.trim() !== '' ||
                (editingIndex !== null && editingValue !== stateSettings?.[editingIndex]?.name)
            }
            closeOnEscape
            header={
                <div className="state-modal__header">
                    <MdViewWeek size={22} />
                    <span>Список состояний</span>
                </div>
            }
            footer={
                <div className="state-modal__footer">
                    <Button variant="secondary" size="sm" onClick={props.onClose}>
                        Закрыть
                    </Button>
                </div>
            }
        >
            <div className="state-modal__layout">
                <div className="state-modal__intro">
                    {selectedState ? (
                        <span>
                            Текущий вид: <b>{selectedState.name}</b>
                        </span>
                    ) : (
                        <span>Состояние сейчас не выбрано.</span>
                    )}
                    {defaultState && (
                        <span>
                            По умолчанию: <b>{defaultState.name}</b>
                        </span>
                    )}
                </div>

                {selectedState && isModified && (
                    <section className="state-modal__changes" aria-label="Изменения состояния">
                        <b>Изменения в «{selectedState.name}»</b>
                        <ul>
                            {changes?.map((change, index) => (
                                <li key={index}>{change}</li>
                            ))}
                        </ul>
                        <div className="state-modal__changes-actions">
                            <Button
                                variant="secondary"
                                size="sm"
                                disabled={saving}
                                onClick={() => applyState(stateSettings!.indexOf(selectedState))}
                            >
                                Вернуть сохранённое
                            </Button>
                            <Button
                                variant="text"
                                size="sm"
                                disabled={saving}
                                onClick={() => overwriteWithCurrent(stateSettings!.indexOf(selectedState))}
                            >
                                Обновить состояние
                            </Button>
                        </div>
                    </section>
                )}

                <div className="state-modal__compose">
                    <span className="state-modal__compose-icon" aria-hidden>
                        <MdAdd size={20} />
                    </span>
                    <input
                        ref={composeInputRef}
                        className="state-modal__compose-input"
                        type="text"
                        placeholder="Сохранить текущий вид таблицы как…"
                        value={inputNameValue}
                        onChange={(e) => setInputNameValue(e.target.value)}
                        onKeyDown={onComposeKey}
                        aria-label="Название нового состояния"
                    />
                    {!!inputNameValue && <span className="state-modal__compose-kbd">Enter</span>}
                    <Button
                        variant="primary"
                        size="xs"
                        onClick={() => void onSaveState()}
                        disabled={saving || !inputNameValue.trim() || duplicateName}
                        aria-label="Сохранить состояние"
                    >
                        Сохранить
                    </Button>
                </div>

                {showSearch && (
                    <div className="state-modal__search">
                        <span className="state-modal__search-icon" aria-hidden>
                            <MdSearch size={18} />
                        </span>
                        <input
                            className="state-modal__search-input"
                            type="text"
                            placeholder="Поиск по названию"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            aria-label="Поиск состояний"
                        />
                        {searchValue && (
                            <button
                                className="state-modal__icon-btn"
                                type="button"
                                aria-label="Очистить поиск"
                                onClick={() => setSearchValue('')}
                            >
                                <MdClose size={16} />
                            </button>
                        )}
                    </div>
                )}

                {settingsCount === 0 ? (
                    <div className="state-modal__empty">
                        <span className="state-modal__empty-glyph">
                            <MdViewWeek size={26} />
                        </span>
                        <span className="state-modal__empty-title">Пока нет сохранённых видов</span>
                        <span className="state-modal__empty-text">
                            Настройте колонки, фильтры и сортировку в таблице, затем сохраните комбинацию, чтобы
                            переключаться между ней одним кликом.
                        </span>
                    </div>
                ) : filteredList.length === 0 ? (
                    <div className="state-modal__empty-search">
                        <span>
                            Ничего не найдено по запросу <b>«{searchValue}»</b>
                        </span>
                    </div>
                ) : (
                    <ul className="state-modal__list">
                        <AnimatePresence initial={false}>
                            {filteredList.map(({ item, originalIndex }) => renderRow(item, originalIndex))}
                        </AnimatePresence>
                    </ul>
                )}
            </div>
        </Modal>
    );

    return (
        <>
            {modal}
            {confirmModal}
        </>
    );
};

export default StateModal;
